/**
 * Generic slide-in drawer from the right edge.
 *
 * Lifted from NotificationDrawer so it can host any panel
 * (notifications, per-job emails, future side surfaces). Handles the
 * backdrop, escape key, slide animation, and the close affordance.
 * Callers pass a title and body.
 */
import { useEffect, type ReactNode } from 'react';
import { classNames } from '@/lib/format';
import { IconClose } from './Icons';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional sticky footer (e.g. "Mark all read"). */
  footer?: ReactNode;
  /** Width preset. Defaults to the same 26rem the notification panel uses. */
  width?: 'narrow' | 'wide';
  children: ReactNode;
};

export function RightDrawer({
  open,
  onClose,
  title,
  footer,
  width = 'narrow',
  children,
}: Props) {
  // Close on Escape so muscle memory matches the rest of the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const widthClass =
    width === 'wide' ? 'w-full sm:w-[36rem]' : 'w-full sm:w-[26rem]';

  return (
    <>
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={classNames(
          'fixed inset-0 bg-kg-black/30 transition-opacity z-40',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      <aside
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
        className={classNames(
          // Apple glass: the drawer floats over the page, so it gets the
          // real treatment (blur + saturation, top-lit gradient, specular
          // top highlight, deep shadow) instead of an opaque slab with a
          // hairline down its left edge. Only the left corners round —
          // the other three are off-screen against the window edge.
          'kg-pane rounded-none rounded-l-2xl fixed right-0 top-0 h-full max-w-full z-50 transition-transform flex flex-col',
          widthClass,
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header and footer are told apart from the scrolling body by a
            change in ground, not by a rule. */}
        <header className="px-6 h-16 flex items-center justify-between shrink-0 bg-white/40 dark:bg-white/[0.05]">
          <h2 className="kg-h2">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()}`}
            className="text-kg-grey-700 dark:text-kg-grey-300 hover:text-kg-black dark:hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kg-black p-1"
          >
            <IconClose className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">{children}</div>

        {footer && (
          <div className="px-6 py-3 shrink-0 bg-white/40 dark:bg-white/[0.05]">
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}
